import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import type { Period } from "@/components/PeriodTabs";

import { apiGet, apiPost } from "./client";

/**
 * AI uclari — sozlesme §4 (backend/app/core/api/ai.py):
 *  - GET  /ozet/ai?days=      → son kayitli not (uretmez)
 *  - POST /ozet/ai?days=&sync=1&force=0|1 → senkron uret (dugme: "Yeniden uret")
 *  - POST /ai/analyze/{use_case} → kart/hero AI analizi ve devam sorulari
 *    (kpi_id verilirse backend use_case'i HER ZAMAN free-analyze'e sabitler —
 *    bkz. app/core/api/ai.py `ai_analyze`; bu yuzden kart/hero cagrilari
 *    dogrudan `free-analyze` yoluna gider).
 */

export const aiBriefSchema = z
  .object({
    id: z.number().nullable().optional(),
    days: z.number().nullable().optional(),
    veri_hash: z.string().nullable().optional(),
    markdown: z.string(),
    follow_up: z.array(z.string()).optional(),
    model: z.string().nullable().optional(),
    tokens_in: z.number().nullable().optional(),
    tokens_out: z.number().nullable().optional(),
    cost_try: z.number().nullable().optional(),
    latency_ms: z.number().nullable().optional(),
    generated_at: z.string().nullable().optional(),
    kaynak: z.string().nullable().optional(),
  })
  .passthrough();
export type AiBrief = z.infer<typeof aiBriefSchema>;

export const ozetAiGetResponseSchema = z.object({
  status: z.union([z.literal("ready"), z.literal("none")]),
  brief: aiBriefSchema.nullable(),
  stale: z.boolean(),
  days: z.number(),
});
export type OzetAiGetResponse = z.infer<typeof ozetAiGetResponseSchema>;

export const ozetAiPostResponseSchema = z.union([
  z
    .object({
      status: z.literal("ready"),
      brief: aiBriefSchema,
      cached: z.boolean().optional(),
      stale: z.boolean().optional(),
      days: z.number().optional(),
    })
    .passthrough(),
  z.object({
    status: z.union([z.literal("queued"), z.literal("already_queued")]),
    job_id: z.number().nullable(),
    days: z.number(),
  }),
]);
export type OzetAiPostResponse = z.infer<typeof ozetAiPostResponseSchema>;

export function fetchAiBrief(days: Period): Promise<OzetAiGetResponse> {
  return apiGet(`/api/v2/ozet/ai?days=${days}`, ozetAiGetResponseSchema);
}

export function ureteAiBrief(days: Period, force = false): Promise<OzetAiPostResponse> {
  return apiPost(`/api/v2/ozet/ai?days=${days}&sync=1&force=${force ? 1 : 0}`, ozetAiPostResponseSchema);
}

export function aiBriefQueryKey(days: Period) {
  return ["ai", "ozet", days] as const;
}

export function useAiBrief(days: Period) {
  return useQuery({
    queryKey: aiBriefQueryKey(days),
    queryFn: () => fetchAiBrief(days),
  });
}

export function useAiBriefUret() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ days, force = false }: { days: Period; force?: boolean }) => ureteAiBrief(days, force),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: aiBriefQueryKey(vars.days) });
    },
  });
}

// ---------------------------------------------------------------------------
// POST /ai/analyze/{use_case}
// ---------------------------------------------------------------------------

export const aiAnalyzeResponseSchema = z
  .object({
    response: z
      .object({
        answer_markdown: z.string().optional(),
        follow_up_prompts: z.array(z.string()).optional(),
      })
      .passthrough()
      .optional(),
  })
  .passthrough();
export type AiAnalyzeResponse = z.infer<typeof aiAnalyzeResponseSchema>;

export interface AiAnalyzePayload {
  kpi_id?: string;
  days?: number;
  modal_rows?: unknown[];
  user_prompt?: string;
  context?: Record<string, unknown>;
  period?: string;
  period_start?: string;
  period_end?: string;
}

export function postAiAnalyze(useCase: string, payload: AiAnalyzePayload): Promise<AiAnalyzeResponse> {
  return apiPost(`/api/v2/ai/analyze/${useCase}`, aiAnalyzeResponseSchema, payload);
}

export function useAiAnalyze(useCase: string) {
  return useMutation({
    mutationFn: (payload: AiAnalyzePayload) => postAiAnalyze(useCase, payload),
  });
}
